import { omit } from 'lodash-es';
import { z } from 'zod';

import { transformValidator } from '../../validators';
import {
  type DatabaseFieldType,
  type DatabaseValue,
  databaseValueValidator,
} from '../types';

export enum DatabaseFilterType {
  AND = 'and',
  OR = 'or',
  EQUALS = 'equals',
  CONTAINS = 'contains',
  EMPTY = 'empty',
  GREATER = 'greater',
  LESS = 'less',
}

/** Any filter type that filters the value of a specific field. */
export type DatabaseFieldFilterType =
  | DatabaseFilterType.EQUALS
  | DatabaseFilterType.CONTAINS
  | DatabaseFilterType.EMPTY
  | DatabaseFilterType.GREATER
  | DatabaseFilterType.LESS;

export type DatabaseFilterUsageType = DatabaseFilterType.AND | DatabaseFilterType.OR;
export type DatabaseFilterNonBooleanExpression = Exclude<
  DatabaseFilter,
  { type: DatabaseFilterUsageType }
>;
export type DatabaseFilterBooleanExpression = Extract<
  DatabaseFilter,
  { type: DatabaseFilterUsageType }
>;

export const isFilterWithUsageType = (
  filter: DatabaseFilter
): filter is DatabaseFilterBooleanExpression => {
  return filter.type === DatabaseFilterType.AND || filter.type === DatabaseFilterType.OR;
};

export type DatabaseFilter<ExtraPageReferenceType = object> =
  | {
      id: string;
      type: DatabaseFilterUsageType;
      value: DatabaseFilter[];
    }
  | {
      id: string;
      type: DatabaseFilterType.EQUALS;
      not?: boolean;
      fieldId: string;
      value: DatabaseValue<DatabaseFieldType, ExtraPageReferenceType>;
    }
  | {
      id: string;
      type: DatabaseFilterType.CONTAINS;
      not?: boolean;
      fieldId: string;
      value: string;
    }
  | {
      id: string;
      type: DatabaseFilterType.EMPTY;
      not?: boolean;
      fieldId: string;
    }
  | {
      id: string;
      type: DatabaseFilterType.GREATER;
      orEqual?: boolean;
      fieldId: string;
      value: DatabaseValue<DatabaseFieldType, ExtraPageReferenceType>;
    }
  | {
      id: string;
      type: DatabaseFilterType.LESS;
      orEqual?: boolean;
      fieldId: string;
      value: DatabaseValue<DatabaseFieldType, ExtraPageReferenceType>;
    }; // When adding something here, remember to also add it to the validator below

// We have to declare the type and validator separately, because the AND type contains a recursive type reference
export const databaseFilterValidator: z.ZodSchema<DatabaseFilter> = z.union([
  z.object({
    id: z.string(),
    type: z.literal(DatabaseFilterType.AND),
    value: z.array(z.lazy(() => databaseFilterValidator)),
  }),
  z.object({
    id: z.string(),
    type: z.literal(DatabaseFilterType.OR),
    value: z.array(z.lazy(() => databaseFilterValidator)),
  }),
  z.object({
    id: z.string(),
    type: z.literal(DatabaseFilterType.EQUALS),
    not: z.boolean().optional(),
    fieldId: z.string(),
    value: databaseValueValidator,
  }),
  z.object({
    id: z.string(),
    type: z.literal(DatabaseFilterType.CONTAINS),
    not: z.boolean().optional(),
    fieldId: z.string(),
    value: z.string(),
  }),
  z.object({
    id: z.string(),
    type: z.literal(DatabaseFilterType.EMPTY),
    not: z.boolean().optional(),
    fieldId: z.string(),
  }),
  z.object({
    id: z.string(),
    type: z.literal(DatabaseFilterType.GREATER),
    orEqual: z.boolean().optional(),
    fieldId: z.string(),
    value: databaseValueValidator,
  }),
  z.object({
    id: z.string(),
    type: z.literal(DatabaseFilterType.LESS),
    orEqual: z.boolean().optional(),
    fieldId: z.string(),
    value: databaseValueValidator,
  }),
]);

export enum DatabaseGroupSortType {
  ASCENDING = 'asc',
  DESCENDING = 'desc',
  MANUAL = 'manual',
}

export const databaseFieldGroupingValidator = z.object({
  fieldId: z.string(),
  sortType: z.nativeEnum(DatabaseGroupSortType),
  manualGroupOrder: z.array(z.string()),
});

export type DatabaseFieldGrouping = z.infer<typeof databaseFieldGroupingValidator>;

export const databaseGroupingValidator = z.object({
  groupBy: databaseFieldGroupingValidator.optional(),
  subgroupBy: databaseFieldGroupingValidator.optional(),
});

export type DatabaseGrouping = z.infer<typeof databaseGroupingValidator>;

/**
 * A virtual field ID that defines the visibility for fields that are not explicity listed in the fields map.
 * The default used to be to show fields that are not explicitly listed, now the default is to hide them.
 * For field selections that don't have this value set, true should be assumed. For newly created field selections,
 * this value should always be set to false.
 */
export const DEFAULT_FIELD_VISIBILITY_ID = 'default';

/**
 * Returns true if the given field should be visible considering the given field selection.
 */
export function shouldShowField(
  fields: DatabaseViewSettings['fields'],
  fieldId: string
): boolean {
  return !fields || (fields[fieldId] ?? fields[DEFAULT_FIELD_VISIBILITY_ID] ?? true);
}

export enum DatabaseLayoutType {
  TABLE = 'table',
  GALLERY = 'gallery',
  BOARD = 'board',
  CALENDAR = 'calendar',
}

export enum DatabaseCardWidth {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

export const MIN_CARD_WIDTH: Record<DatabaseCardWidth, number> = {
  [DatabaseCardWidth.SMALL]: 250,
  [DatabaseCardWidth.MEDIUM]: 310,
  [DatabaseCardWidth.LARGE]: 410,
};

export enum DatabaseCoverFieldType {
  NONE = 'none',
  /** Take the cover field configured in the database settings */
  DEFAULT = 'default',
}

export enum DatabaseCalculationType {
  SUM = 'sum',
  COUNT_TOTAL = 'countTotal',
  COUNT = 'count',
  COUNT_EMPTY = 'countEmpty',
  COUNT_UNIQUE = 'countUnique',
  PERCENT_EMPTY = 'percentEmpty',
  PERCENT_NOT_EMPTY = 'percentNotEmpty',
  AVERAGE = 'average',
  MAX = 'max',
  MIN = 'min',
}

export const databaseLayoutValidator = z.object({
  type: z.nativeEnum(DatabaseLayoutType),
  cardWidth: z.nativeEnum(DatabaseCardWidth).default(DatabaseCardWidth.MEDIUM),
  coverFieldId: z
    .nativeEnum(DatabaseCoverFieldType)
    .or(z.string())
    .default(DatabaseCoverFieldType.DEFAULT),
  showFieldNames: z.boolean().default(true),
  /** @deprecated Use `grouping` in view settings instead. */
  groupFieldId: z.string().optional(),
  dateFieldId: z.string().optional(),
  showNumberedEntries: z.boolean().default(true),
});

export type DatabaseLayout = z.infer<typeof databaseLayoutValidator>;

/**
 * All these ViewId values have been deprecated but are kept around for compatibility reasons.
 */
export enum ViewId {
  /** The ID for the default view, used during EAP. */
  DEFAULT = 'default',
  /** The ID for the initial view ("All entries") that is created on each database */
  ALL_ENTRIES = 'all-entries',
  /** If a view contains the 'unsavedView' query param */
  UNSAVED = 'unsaved-view',
}

const rawDatabaseViewSettingsValidator = z.object({
  /**
   * Fields that should be shown, mapping the field ID to a boolean. If a particular field is not defined
   * here, the default is true. This is to make sure that when a new field is added while a database view
   * limits the number of fields shown, the new field is shown as well.
   */
  fields: z.record(z.boolean()).optional(),

  filter: databaseFilterValidator.optional(),

  sort: z
    .object({
      ascending: z.boolean().default(true),
      fieldId: z.string(),
    })
    .optional(),

  layout: databaseLayoutValidator,

  search: z.string().optional(),

  calculations: z.record(z.nativeEnum(DatabaseCalculationType)).optional(),

  sorts: z
    .array(
      z.object({
        ascending: z.boolean().default(true),
        fieldId: z.string(),
      })
    )
    .default([]),

  grouping: databaseGroupingValidator.optional(),
});

export const databaseViewSettingsValidator = transformValidator(
  rawDatabaseViewSettingsValidator.extend({
    viewMode: databaseLayoutValidator.shape.type.optional(),
    layout: rawDatabaseViewSettingsValidator.shape.layout.optional(),
  }),
  (view): DatabaseViewSettings => {
    // Transform legacy "viewMode" property to "layout.type"

    const clearedFields = omit(view.fields ?? {}, 'cover');

    return {
      ...omit(view, ['viewMode', 'fields']),
      layout:
        view.layout ||
        databaseLayoutValidator.parse({
          type: view.viewMode || DatabaseLayoutType.TABLE,
          // In the legacy view mode, a virtual "cover" field could be enabled/disabled in the field selection
          coverFieldId: shouldShowField(view.fields, 'cover')
            ? DatabaseCoverFieldType.DEFAULT
            : DatabaseCoverFieldType.NONE,
        }),
      ...(Object.keys(clearedFields).length > 0 ? { fields: clearedFields } : {}),
    };
  },
  rawDatabaseViewSettingsValidator
);

/**
 * The settings for a particular database view, such as a selection of fields and a sort mode.
 */
export type DatabaseViewSettings = z.infer<typeof rawDatabaseViewSettingsValidator>;

export const DEFAULT_DATABASE_VIEW_SETTINGS: DatabaseViewSettings = {
  layout: databaseLayoutValidator.parse({
    type: DatabaseLayoutType.TABLE,
  }),
  sorts: [],
};

export const databaseViewValidator = z.object({
  /**
   * The ID of the view. For saved views, this is a random generated ID.
   * For an unsaved view, which has since been unshipped but kept for compatibility reasons, ViewId.UNSAVED
   */
  id: z.string(),
  name: z.string(),
  settings: rawDatabaseViewSettingsValidator
    .omit({ search: true })
    .default(DEFAULT_DATABASE_VIEW_SETTINGS),
});

export type DatabaseView = z.infer<typeof databaseViewValidator>;
